import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { guidedTourClasses, type Tour } from '@dhl/guided-tour'
import { useThemedNativeBackdrop } from '@dhl/native-modal'

import type { CompletedTour } from '@dhl/receiver-profile-api'
import { isNative } from '@dhl/webview'
import { device } from '@dhl/utils'

import { links } from '~/links.js'
import { useGetProfileCompletedInteractions } from '~/profile/model/profile/hooks.js'

const getNativeTopMargin = (): number | undefined => {
  const isNativeIos = isNative() && device.isIOS

  return isNativeIos ? -48 : undefined
}

export const useGetGuidedTours = (): {
  tours: Tour[]
  getGuidedTours: () => Promise<Tour[]>
} => {
  const { t } = useTranslation()
  const { data: profileCompletedInteractions, refetch } = useGetProfileCompletedInteractions()
  const { hideNativeBackdrop, showNativeBackdrop } = useThemedNativeBackdrop()
  const location = useLocation()

  const getTours = useCallback(
    (data: CompletedTour[] | undefined) => {
      const completedTours = data?.map((item) => item.name) ?? []

      const configuredTours = [
        {
          name: 'portal-dashboard-profile',
          showOnPaths: [links.dashboardLink()],
          steps: [
            {
              target: '[data-test="profile-menu-button"]',
              title: t('portal.guidedTour.profile.0.title'),
              body: t('portal.guidedTour.profile.0.body'),
              highlightElement: '.MuiAppBar-root',
              onShow: () => showNativeBackdrop({ visibleOnFooter: true, visibleOnHeader: false }),
              onHide: hideNativeBackdrop,
            },
          ],
          sx: {
            [`& .${guidedTourClasses.focus}`]: {
              top: getNativeTopMargin(),
            },
          },
        },
      ].filter(Boolean) as Tour[]

      return configuredTours.filter(
        (tour) =>
          !completedTours?.includes(tour.name ?? '') &&
          tour.showOnPaths.some((path) => location.pathname.endsWith(path))
      )
    },
    [hideNativeBackdrop, location.pathname, showNativeBackdrop, t]
  )

  const tours = useMemo(() => getTours(profileCompletedInteractions), [getTours, profileCompletedInteractions])

  const getGuidedTours = async (): Promise<Tour[]> => {
    const { data: nextProfileCompletedInteractions } = await refetch()
    return Promise.resolve(getTours(nextProfileCompletedInteractions))
  }

  return {
    tours,
    getGuidedTours,
  }
}
